import { useEffect, useRef, useState } from "react";

/**
 * LazyImage — drop-in replacement for <img> that:
 *  - Defers image network request until the element enters the viewport (IntersectionObserver)
 *  - Renders a shimmering skeleton placeholder while loading
 *  - Fades the bitmap in once decoded
 *  - Gracefully keeps the placeholder if the source errors out
 *
 * Use this anywhere a list/grid of many images would otherwise queue dozens of
 * heavy PNG/JPG requests at once (testimonials, clients, certificates, gallery
 * thumbnails, product variant grids).
 *
 * Props mirror <img> where reasonable; `containerClassName` styles the outer
 * wrapper (which holds the skeleton), `className` styles the inner image.
 */
const LazyImage = ({
  src,
  alt,
  className = "",
  containerClassName = "",
  width,
  height,
  rootMargin = "200px",
  fetchPriority = "low",
  decoding = "async",
  loading = "lazy",
  priority = false,
  onClick,
  ...rest
}) => {
  const ref = useRef(null);
  // When `priority` is true (above-the-fold / LCP candidate), bypass the
  // IntersectionObserver and render the image immediately with eager loading.
  const [inView, setInView] = useState(priority);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (priority) return;
    const node = ref.current;
    if (!node || inView) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [inView, rootMargin, priority]);

  const effectiveLoading = priority ? "eager" : loading;
  const effectiveFetchPriority = priority ? "high" : fetchPriority;
  // Auto-serve WebP fallback for local raster images. Any src like "/foo.png"
  // or "/foo.jpg" has a matching .webp sibling generated at build time — the
  // browser picks whichever it can render. Skips SVGs, external URLs, and
  // paths without a raster extension.
  //
  // IMPORTANT: The <source> AND <img> must be LITERAL direct children of <picture>.
  // Dev-mode JSX instrumentation wraps both (a) conditionally-rendered elements
  // AND (b) variable-interpolated JSX elements in a <span data-ve-dynamic>. Either
  // wrapper breaks <picture> semantics (browser silently falls back to <img src>).
  // So we duplicate the <img> tag literally across both branches instead of
  // extracting it to a variable.
  const isLocalRaster = typeof src === "string"
    && src.startsWith("/")
    && /\.(png|jpe?g)$/i.test(src);
  const webpSrc = isLocalRaster ? src.replace(/\.(png|jpe?g)$/i, ".webp") : null;

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${containerClassName}`}
      onClick={onClick}
      style={width && height ? { width, height } : undefined}
    >
      {!loaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-[#F1F5F9] to-[#E2E8F0] animate-pulse" />
      )}
      {inView && webpSrc && (
        <picture>
          <source srcSet={webpSrc} type="image/webp" />
          <img
            src={src}
            alt={alt}
            width={width}
            height={height}
            loading={effectiveLoading}
            decoding={decoding}
            fetchPriority={effectiveFetchPriority}
            onLoad={() => setLoaded(true)}
            onError={() => setLoaded(true)}
            className={`transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"} ${className}`}
            {...rest}
          />
        </picture>
      )}
      {inView && !webpSrc && (
        <img
          src={src}
          alt={alt}
          width={width}
          height={height}
          loading={effectiveLoading}
          decoding={decoding}
          fetchPriority={effectiveFetchPriority}
          onLoad={() => setLoaded(true)}
          onError={() => setLoaded(true)}
          className={`transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"} ${className}`}
          {...rest}
        />
      )}
    </div>
  );
};

export default LazyImage;
